/**
 * SelectionOverlay - Shows other users' selected shapes
 *
 * Displays a colored border around shapes that other users have selected,
 * with a label showing who is editing the shape.
 */

import React, { useMemo } from 'react';
import { Shape } from '../../types/Shape';
import { CollaborationUser, ShapeLock } from '../../types/Collaboration';

/**
 * Props for SelectionOverlay component
 */
interface SelectionOverlayProps {
  /** All shapes in the canvas */
  shapes: Shape[];
  /** Remote users (excluding current user) */
  remoteUsers: CollaborationUser[];
  /** Currently locked shapes */
  lockedShapes: Map<string, ShapeLock>;
  /** Current user ID (to exclude from display) */
  currentUserId?: string;
  /** Stage position */
  stagePos: { x: number; y: number };
  /** Stage scale */
  stageScale: number;
}

/**
 * Selection info for a shape
 */
interface ShapeSelection {
  shapeId: string;
  userId: string;
  userName: string;
  userColor: string;
  isLocked: boolean;
  lockType?: string;
}

/**
 * Single shape selection indicator
 */
interface SelectionIndicatorProps {
  shape: Shape;
  selection: ShapeSelection;
  stagePos: { x: number; y: number };
  stageScale: number;
}

function SelectionIndicator({
  shape,
  selection,
  stagePos,
  stageScale,
}: SelectionIndicatorProps) {
  // Calculate shape bounds
  const bounds = useMemo(() => {
    let x = shape.x;
    let y = shape.y;
    let width = shape.width || 100;
    let height = shape.height || 100;

    // Handle circle
    if (shape.type === 'circle' && shape.radius) {
      width = shape.radius * 2;
      height = shape.radius * 2;
    }

    // Handle arrow
    if (shape.type === 'arrow') {
      const endX = x + (shape.width || 0);
      const endY = y + (shape.height || 0);
      const minX = Math.min(x, endX);
      const minY = Math.min(y, endY);
      const maxX = Math.max(x, endX);
      const maxY = Math.max(y, endY);
      x = minX;
      y = minY;
      width = maxX - minX;
      height = maxY - minY;
    }

    return { x, y, width, height };
  }, [shape]);

  // Screen position
  const screenX = bounds.x * stageScale + stagePos.x;
  const screenY = bounds.y * stageScale + stagePos.y;
  const screenWidth = bounds.width * stageScale;
  const screenHeight = bounds.height * stageScale;

  // Don't render if off-screen
  if (
    screenX + screenWidth < 0 ||
    screenY + screenHeight < 0 ||
    screenX > window.innerWidth ||
    screenY > window.innerHeight
  ) {
    return null;
  }

  const borderWidth = 2;
  const labelOffset = -24;

  return (
    <div
      style={{
        position: 'absolute',
        left: screenX - borderWidth,
        top: screenY - borderWidth,
        width: screenWidth + borderWidth * 2,
        height: screenHeight + borderWidth * 2,
        border: `${borderWidth}px solid ${selection.userColor}`,
        borderRadius: shape.type === 'circle' ? '50%' : '4px',
        pointerEvents: 'none',
        zIndex: 9990,
        boxSizing: 'border-box',
      }}
    >
      {/* User label */}
      <div
        style={{
          position: 'absolute',
          top: labelOffset,
          left: 0,
          backgroundColor: selection.userColor,
          color: 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {selection.isLocked && (
          <span style={{ fontSize: '10px' }}>
            {selection.lockType === 'drag' && '\u2630'}
            {selection.lockType === 'resize' && '\u2922'}
            {selection.lockType === 'edit' && '\u270E'}
            {selection.lockType === 'full' && '\u26BF'}
          </span>
        )}
        {selection.userName}
      </div>
    </div>
  );
}

/**
 * SelectionOverlay component
 *
 * Shows colored borders around shapes that other users have selected or are editing.
 */
export function SelectionOverlay({
  shapes,
  remoteUsers,
  lockedShapes,
  currentUserId,
  stagePos,
  stageScale,
}: SelectionOverlayProps) {
  // Build a map of shape selections from user presence and locks
  const shapeSelections = useMemo(() => {
    const selections = new Map<string, ShapeSelection>();

    // First, add selections from user presence
    for (const user of remoteUsers) {
      if (user.id === currentUserId) continue;
      if (!user.presence?.selection) continue;

      for (const shapeId of user.presence.selection) {
        // Don't overwrite if already has a lock
        if (selections.has(shapeId)) continue;

        selections.set(shapeId, {
          shapeId,
          userId: user.id,
          userName: user.name,
          userColor: user.color,
          isLocked: false,
        });
      }
    }

    // Then, add/update with lock information (higher priority)
    lockedShapes.forEach((lock, shapeId) => {
      if (lock.userId === currentUserId) return;

      selections.set(shapeId, {
        shapeId,
        userId: lock.userId,
        userName: lock.userName,
        userColor: lock.userColor,
        isLocked: true,
        lockType: lock.lockType,
      });
    });

    return selections;
  }, [remoteUsers, lockedShapes, currentUserId]);

  // Get shapes that have selections
  const selectedShapesWithInfo = useMemo(() => {
    const result: Array<{ shape: Shape; selection: ShapeSelection }> = [];

    shapes.forEach((shape) => {
      const selection = shapeSelections.get(shape.id);
      if (selection) {
        result.push({ shape, selection });
      }
    });

    return result;
  }, [shapes, shapeSelections]);

  if (selectedShapesWithInfo.length === 0) {
    return null;
  }

  return (
    <>
      {selectedShapesWithInfo.map(({ shape, selection }) => (
        <SelectionIndicator
          key={`selection-${shape.id}`}
          shape={shape}
          selection={selection}
          stagePos={stagePos}
          stageScale={stageScale}
        />
      ))}
    </>
  );
}

export default SelectionOverlay;
